"use client";

import { getLocalTimeZone, today } from "@internationalized/date";
import { useControlledState } from "@react-stately/utils";
import { Calendar as CalendarIcon } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useDateFormatter } from "react-aria";
import type {
	DatePickerProps as AriaDatePickerProps,
	DateValue,
} from "react-aria-components";
import {
	DatePicker as AriaDatePicker,
	Dialog as AriaDialog,
	Group as AriaGroup,
	Popover as AriaPopover,
} from "react-aria-components";
import { Button, type ButtonProps } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import { Calendar } from "./calendar";
import {
	POPOVER_DEFAULT_OFFSET_PX,
	POPOVER_VIEWPORT_PADDING_PX,
} from "./fit-popover-in-viewport";
import { useFitPopoverInViewport } from "./use-fit-popover-in-viewport";

const highlightedDates = [today(getLocalTimeZone())];

function formatDateDdMmYyyy(date: DateValue): string {
	const day = String(date.day).padStart(2, "0");
	const month = String(date.month).padStart(2, "0");
	return `${day}/${month}/${date.year}`;
}

type DatePickerProps = AriaDatePickerProps<DateValue> & {
	/** Called when Apply is clicked, with the committed date. */
	onApply?: (date: DateValue | null) => void;
	/** Called when Cancel is clicked. */
	onCancel?: () => void;
	/** Show date as dd/mm/yyyy on the trigger. Default: locale formatter (also dd/mm/yyyy). */
	displayFormatDdMmYyyy?: boolean;
	size?: ButtonProps["size"];
};

export function DatePicker({
	value: valueProp,
	defaultValue,
	onChange,
	onApply,
	onCancel,
	onOpenChange,
	displayFormatDdMmYyyy = false,
	size = "md",
	...props
}: DatePickerProps) {
	const popoverRef = useRef<HTMLElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const formatter = useDateFormatter({
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
	const [value, setValue] = useControlledState(
		valueProp,
		defaultValue || null,
		onChange
	);
	const [tempValue, setTempValue] = useState(value);

	useEffect(() => {
		setTempValue(value);
	}, [value]);

	useFitPopoverInViewport({
		align: "end",
		isOpen,
		offsetPx: POPOVER_DEFAULT_OFFSET_PX,
		popoverRef,
		triggerRef,
	});

	const formattedDate = (() => {
		if (!value) {
			return "Selecionar data";
		}
		if (displayFormatDdMmYyyy) {
			return formatDateDdMmYyyy(value);
		}
		return formatter.format(value.toDate(getLocalTimeZone()));
	})();

	return (
		<AriaDatePicker
			aria-label="Date picker"
			shouldCloseOnSelect={false}
			{...props}
			onChange={setTempValue}
			onOpenChange={(open) => {
				if (open) {
					setTempValue(value);
				}
				setIsOpen(open);
				onOpenChange?.(open);
			}}
			value={tempValue}
		>
			<AriaGroup ref={triggerRef}>
				<Button
					className="rounded-md"
					color="secondary"
					iconLeading={CalendarIcon}
					size={size}
					style={{ width: "100%" }}
				>
					{formattedDate}
				</Button>
			</AriaGroup>
			<AriaPopover
				className={({ isEntering, isExiting }) =>
					cx(
						"origin-(--trigger-anchor-point) will-change-transform",
						"z-9999 max-h-none! overflow-visible",
						!!isEntering &&
							"fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5 animate-in duration-150 ease-out",
						!!isExiting &&
							"fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5 animate-out duration-100 ease-in"
					)
				}
				containerPadding={POPOVER_VIEWPORT_PADDING_PX}
				offset={POPOVER_DEFAULT_OFFSET_PX}
				placement="bottom right"
				ref={popoverRef}
				shouldFlip
				UNSTABLE_portalContainer={
					typeof document === "undefined" ? undefined : document.body
				}
			>
				<AriaDialog
					aria-label="Date picker"
					className="rounded-2xl bg-primary shadow-xl ring ring-secondary_alt"
				>
					{({ close }) => (
						<>
							<div className="flex px-6 py-5">
								<Calendar highlightedDates={highlightedDates} />
							</div>
							<div className="grid grid-cols-2 gap-3 border-secondary border-t p-4">
								<Button
									color="secondary"
									onClick={() => {
										setTempValue(value);
										onCancel?.();
										close();
									}}
									size="md"
								>
									Cancelar
								</Button>
								<Button
									color="primary"
									onClick={() => {
										setValue(tempValue);
										onApply?.(tempValue);
										close();
									}}
									size="md"
								>
									Aplicar
								</Button>
							</div>
						</>
					)}
				</AriaDialog>
			</AriaPopover>
		</AriaDatePicker>
	);
}
